/**
 * Date/Time Configuration
 * 
 * Centralized configuration for all date and time formatting across the application.
 * Provides locale-aware formatting, timezone handling, and relative time displays.
 */

// Type definitions
export interface DateTimeConfig {
  defaultLocale: string;
  defaultTimezone: string;
  formats: DateTimeFormats;
  locales: LocaleFormats;
  relative: RelativeTimeConfig;
  calendar: CalendarConfig;
  parsing: ParsingConfig;
  display: DisplayConfig;
}

export interface DateTimeFormats {
  date: {
    short: string;      // "12/31/23"
    medium: string;     // "Dec 31, 2023"
    long: string;       // "December 31, 2023"
    full: string;       // "Sunday, December 31, 2023"
  };
  time: {
    short: string;      // "3:30 PM"
    medium: string;     // "3:30:45 PM"
    long: string;       // "3:30:45 PM EST"
    full: string;       // "3:30:45 PM Eastern Standard Time"
  };
  datetime: {
    short: string;      // "12/31/23, 3:30 PM"
    medium: string;     // "Dec 31, 2023, 3:30 PM"
    long: string;       // "December 31, 2023 at 3:30 PM EST"
    full: string;       // "Sunday, December 31, 2023 at 3:30:45 PM Eastern Standard Time"
  };
  custom: {
    iso: string;        // ISO 8601
    rfc: string;        // RFC 2822
    unix: string;       // Unix timestamp
    sql: string;        // SQL datetime
    filename: string;   // Safe for filenames
  };
}

export interface LocaleFormats {
  [locale: string]: {
    dateFormat: string;
    timeFormat: string;
    firstDayOfWeek: number; // 0 = Sunday, 1 = Monday
    meridiem: [string, string]; // ["AM", "PM"]
    monthNames: string[];
    monthNamesShort: string[];
    dayNames: string[];
    dayNamesShort: string[];
    dayNamesMin: string[];
  };
}

export interface RelativeTimeConfig {
  enabled: boolean;
  thresholds: {
    seconds: number;    // Show seconds up to X
    minutes: number;    // Show minutes up to X
    hours: number;      // Show hours up to X
    days: number;       // Show days up to X
    weeks: number;      // Show weeks up to X
    months: number;     // Show months up to X
  };
  formats: {
    future: string;     // "in %s"
    past: string;       // "%s ago"
    justNow: string;    // "just now"
    today: string;      // "Today at %s"
    yesterday: string;  // "Yesterday at %s"
    tomorrow: string;   // "Tomorrow at %s"
  };
  updateInterval: number; // How often to update relative times (ms)
}

export interface CalendarConfig {
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabledDaysOfWeek?: number[];
  holidays?: Holiday[];
}

export interface Holiday {
  date: string;
  name: string;
  type: 'public' | 'observance' | 'optional';
}

export interface ParsingConfig {
  strict: boolean;
  formats: string[];
  timezone: 'local' | 'utc' | string;
}

export interface DisplayConfig {
  showTimezone: boolean;
  use24Hour: boolean;
  showSeconds: boolean;
  showWeekNumbers: boolean;
  highlightToday: boolean;
  highlightWeekends: boolean;
}

/**
 * Main date/time configuration
 */
export const dateTimeConfig: DateTimeConfig = {
  // Default settings
  defaultLocale: 'en-US',
  defaultTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

  // Standard format patterns
  formats: {
    date: {
      short: 'MM/dd/yy',
      medium: 'MMM dd, yyyy',
      long: 'MMMM dd, yyyy',
      full: 'EEEE, MMMM dd, yyyy',
    },
    time: {
      short: 'h:mm a',
      medium: 'h:mm:ss a',
      long: 'h:mm:ss a zzz',
      full: 'h:mm:ss a zzzz',
    },
    datetime: {
      short: 'MM/dd/yy, h:mm a',
      medium: 'MMM dd, yyyy, h:mm a',
      long: 'MMMM dd, yyyy \'at\' h:mm a zzz',
      full: 'EEEE, MMMM dd, yyyy \'at\' h:mm:ss a zzzz',
    },
    custom: {
      iso: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX',
      rfc: 'EEE, dd MMM yyyy HH:mm:ss zzz',
      unix: 'X',
      sql: 'yyyy-MM-dd HH:mm:ss',
      filename: 'yyyy-MM-dd_HH-mm-ss',
    },
  },

  // Locale-specific formats
  locales: {
    'en-US': {
      dateFormat: 'MM/dd/yyyy',
      timeFormat: 'h:mm a',
      firstDayOfWeek: 0, // Sunday
      meridiem: ['AM', 'PM'],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
      dayNames: [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
      ],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    },
    'en-GB': {
      dateFormat: 'dd/MM/yyyy',
      timeFormat: 'HH:mm',
      firstDayOfWeek: 1, // Monday
      meridiem: ['AM', 'PM'],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
      dayNames: [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
      ],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    },
    'de-DE': {
      dateFormat: 'dd.MM.yyyy',
      timeFormat: 'HH:mm',
      firstDayOfWeek: 1, // Monday
      meridiem: ['', ''], // 24-hour format
      monthNames: [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
        'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
      ],
      dayNames: [
        'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'
      ],
      dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    },
    'fr-FR': {
      dateFormat: 'dd/MM/yyyy',
      timeFormat: 'HH:mm',
      firstDayOfWeek: 1, // Monday
      meridiem: ['', ''], // 24-hour format
      monthNames: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ],
      monthNamesShort: [
        'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
        'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
      ],
      dayNames: [
        'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'
      ],
      dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
    },
    'es-ES': {
      dateFormat: 'dd/MM/yyyy',
      timeFormat: 'HH:mm',
      firstDayOfWeek: 1, // Monday
      meridiem: ['', ''], // 24-hour format
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      monthNamesShort: [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
      ],
      dayNames: [
        'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'
      ],
      dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
      dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
    },
    'ja-JP': {
      dateFormat: 'yyyy年MM月dd日',
      timeFormat: 'HH:mm',
      firstDayOfWeek: 0, // Sunday
      meridiem: ['午前', '午後'],
      monthNames: [
        '1月', '2月', '3月', '4月', '5月', '6月',
        '7月', '8月', '9月', '10月', '11月', '12月'
      ],
      monthNamesShort: [
        '1月', '2月', '3月', '4月', '5月', '6月',
        '7月', '8月', '9月', '10月', '11月', '12月'
      ],
      dayNames: [
        '日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'
      ],
      dayNamesShort: ['日', '月', '火', '水', '木', '金', '土'],
      dayNamesMin: ['日', '月', '火', '水', '木', '金', '土'],
    },
  },

  // Relative time configuration
  relative: {
    enabled: true,
    thresholds: {
      seconds: 60,        // Show "X seconds ago" up to 60 seconds
      minutes: 60,        // Show "X minutes ago" up to 60 minutes
      hours: 24,          // Show "X hours ago" up to 24 hours
      days: 7,            // Show "X days ago" up to 7 days
      weeks: 4,           // Show "X weeks ago" up to 4 weeks
      months: 12,         // Show "X months ago" up to 12 months
    },
    formats: {
      future: 'in %s',
      past: '%s ago',
      justNow: 'just now',
      today: 'Today at %s',
      yesterday: 'Yesterday at %s',
      tomorrow: 'Tomorrow at %s',
    },
    updateInterval: 60000, // Update relative times every minute
  },

  // Calendar configuration
  calendar: {
    weekStartsOn: 0, // Sunday
    minDate: new Date('1900-01-01'),
    maxDate: new Date('2100-12-31'),
    disabledDaysOfWeek: [], // e.g., [0, 6] for weekends
    holidays: [
      { date: '2024-01-01', name: "New Year's Day", type: 'public' },
      { date: '2024-07-04', name: 'Independence Day', type: 'public' },
      { date: '2024-12-25', name: 'Christmas Day', type: 'public' },
      // Add more holidays as needed
    ],
  },

  // Parsing configuration
  parsing: {
    strict: false,
    formats: [
      'MM/dd/yyyy',
      'MM-dd-yyyy',
      'yyyy-MM-dd',
      'dd/MM/yyyy',
      'dd.MM.yyyy',
      'MMM dd, yyyy',
      'MMMM dd, yyyy',
    ],
    timezone: 'local',
  },

  // Display configuration
  display: {
    showTimezone: false,
    use24Hour: false,
    showSeconds: false,
    showWeekNumbers: false,
    highlightToday: true,
    highlightWeekends: true,
  },
};

/**
 * Utility function to format a date according to locale and format
 */
export function formatDate(
  date: Date | string | number,
  format: keyof DateTimeFormats['date'] = 'medium',
  locale: string = dateTimeConfig.defaultLocale
): string {
  const dateObj = new Date(date);

  // Use Intl.DateTimeFormat for locale-aware formatting
  const options = getIntlOptions(format, 'date');
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}

/**
 * Utility function to format a time according to locale and format
 */
export function formatTime(
  date: Date | string | number,
  format: keyof DateTimeFormats['time'] = 'short',
  locale: string = dateTimeConfig.defaultLocale
): string {
  const dateObj = new Date(date);
  const options = getIntlOptions(format, 'time');
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}

/**
 * Utility function to format a datetime according to locale and format
 */
export function formatDateTime(
  date: Date | string | number,
  format: keyof DateTimeFormats['datetime'] = 'medium',
  locale: string = dateTimeConfig.defaultLocale
): string {
  const dateObj = new Date(date);
  const options = getIntlOptions(format, 'datetime');
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}

/**
 * Get relative time display (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTime(
  date: Date | string | number,
  locale: string = dateTimeConfig.defaultLocale
): string {
  const dateObj = new Date(date);
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffSec = Math.floor(Math.abs(diffMs) / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const config = dateTimeConfig.relative;
  const isPast = diffMs < 0;

  // Just now
  if (diffSec < 5) {
    return config.formats.justNow;
  }

  // Seconds
  if (diffSec < config.thresholds.seconds) {
    const value = `${diffSec} second${diffSec !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Minutes
  if (diffMin < config.thresholds.minutes) {
    const value = `${diffMin} minute${diffMin !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Hours
  if (diffHr < config.thresholds.hours) {
    const value = `${diffHr} hour${diffHr !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Days (with Today/Yesterday/Tomorrow)
  if (diffDays === 0) {
    return config.formats.today.replace('%s', formatTime(dateObj, 'short', locale));
  }
  if (diffDays === 1) {
    return isPast ? config.formats.yesterday.replace('%s', formatTime(dateObj, 'short', locale))
                  : config.formats.tomorrow.replace('%s', formatTime(dateObj, 'short', locale));
  }
  if (diffDays < config.thresholds.days) {
    const value = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Weeks
  if (diffWeeks < config.thresholds.weeks) {
    const value = `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Months
  if (diffMonths < config.thresholds.months) {
    const value = `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
    return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
  }

  // Years
  const value = `${diffYears} year${diffYears !== 1 ? 's' : ''}`;
  return isPast ? config.formats.past.replace('%s', value) : config.formats.future.replace('%s', value);
}

/**
 * Format date for specific context
 */
export function formatForContext(
  date: Date | string | number,
  context: 'display' | 'input' | 'api' | 'filename' | 'iso',
  locale: string = dateTimeConfig.defaultLocale
): string {
  const dateObj = new Date(date);

  switch (context) {
    case 'display':
      return formatDateTime(dateObj, 'medium', locale);
    case 'input':
      return dateObj.toISOString().slice(0, 16); // For datetime-local input
    case 'api':
      return dateObj.toISOString();
    case 'filename':
      return formatCustom(dateObj, dateTimeConfig.formats.custom.filename);
    case 'iso':
      return dateObj.toISOString();
    default:
      return formatDateTime(dateObj, 'medium', locale);
  }
}

/**
 * Parse a date string with multiple format attempts
 */
export function parseDate(
  dateString: string,
  _locale: string = dateTimeConfig.defaultLocale
): Date | null {
  // Try native Date parsing first
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date;
  }

  // Try locale-specific parsing
  // This would need a more robust date parsing library in production
  // For now, return null if parsing fails
  return null;
}

/**
 * Get calendar week number
 */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Check if date is weekend
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Check if date is holiday
 */
export function isHoliday(date: Date): boolean {
  const dateStr = formatCustom(date, 'yyyy-MM-dd');
  return dateTimeConfig.calendar.holidays?.some(h => h.date === dateStr) || false;
}

/**
 * Get timezone offset display
 */
export function getTimezoneDisplay(date: Date = new Date()): string {
  const offset = -date.getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Helper function to get Intl.DateTimeFormat options
 */
function getIntlOptions(
  format: string,
  type: 'date' | 'time' | 'datetime'
): Intl.DateTimeFormatOptions {
  const baseOptions: Record<string, Intl.DateTimeFormatOptions> = {
    // Date formats
    'date.short': { year: '2-digit', month: '2-digit', day: '2-digit' },
    'date.medium': { year: 'numeric', month: 'short', day: 'numeric' },
    'date.long': { year: 'numeric', month: 'long', day: 'numeric' },
    'date.full': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    
    // Time formats
    'time.short': { hour: 'numeric', minute: '2-digit' },
    'time.medium': { hour: 'numeric', minute: '2-digit', second: '2-digit' },
    'time.long': { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' },
    'time.full': { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'long' },
    
    // DateTime formats
    'datetime.short': { 
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: 'numeric', minute: '2-digit'
    },
    'datetime.medium': {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    },
    'datetime.long': {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    },
    'datetime.full': {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'long'
    },
  };

  return baseOptions[`${type}.${format}`] || baseOptions['datetime.medium'];
}

/**
 * Custom format function (simplified - would need a library like date-fns for full support)
 */
function formatCustom(date: Date, format: string): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return format
    .replace('yyyy', year.toString())
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
    .replace('_', '_')
    .replace('-', '-')
    .replace(' ', ' ')
    .replace(':', ':');
}

/**
 * CSS classes for date/time display
 */
export const dateTimeClasses = {
  // Date display
  date: 'app-date',
  dateShort: 'app-date--short',
  dateMedium: 'app-date--medium',
  dateLong: 'app-date--long',
  dateFull: 'app-date--full',
  
  // Time display
  time: 'app-time',
  timeShort: 'app-time--short',
  timeMedium: 'app-time--medium',
  timeLong: 'app-time--long',
  timeFull: 'app-time--full',
  
  // DateTime display
  datetime: 'app-datetime',
  datetimeShort: 'app-datetime--short',
  datetimeMedium: 'app-datetime--medium',
  datetimeLong: 'app-datetime--long',
  datetimeFull: 'app-datetime--full',
  
  // Relative time
  relative: 'app-time-relative',
  relativeRecent: 'app-time-relative--recent',
  relativePast: 'app-time-relative--past',
  relativeFuture: 'app-time-relative--future',
  
  // Calendar elements
  calendar: 'app-calendar',
  calendarToday: 'app-calendar--today',
  calendarWeekend: 'app-calendar--weekend',
  calendarHoliday: 'app-calendar--holiday',
  calendarDisabled: 'app-calendar--disabled',
  
  // Timezone
  timezone: 'app-timezone',
  timezoneOffset: 'app-timezone--offset',
  timezoneName: 'app-timezone--name',
} as const;

export default dateTimeConfig;